import moment from 'moment';

export const getMaxDaysInMonth = (month, year) => {
  const monthInt = parseInt(month, 10);
  const yearInt = parseInt(year, 10);

  if (isNaN(monthInt) || isNaN(yearInt) || monthInt < 1 || monthInt > 12) {
    return 31; // Fallback to 31
  }

  return new Date(yearInt, monthInt, 0).getDate();
};

export const shouldPad = (part, val, digitsOnly) => {
  const num = parseInt(val, 10);
  if (isNaN(num)) return false;
    // console.log('part',part);
  if (part === 'MM') {
    return (val.length === 1 && num >= 2 && num <= 9) || (val.length === 2 && num > 12);
  }

  if (part === 'YYYY') {
    // Auto-pad if year is valid but incomplete (e.g., 1 digit when expecting 4)
    // Or if year exceeds max when partially entered
    const minYear = 1900;
    const maxYear = 2099;
    return (val.length < 4 && num >= minYear && num <= maxYear) || 
           (val.length === 4 && (num < minYear || num > maxYear));
  }

  if (part === 'DD') {
    const monthStr = digitsOnly.slice(0, 2);
    const yearStr = digitsOnly.slice(4, 8); // assuming MMDDYYYY
    const maxDays = getMaxDaysInMonth(monthStr, yearStr);
    return (val.length === 1 && num >= 4) || (val.length === 2 && num > maxDays);
  }
  if (part === 'HH') {
    // Hour: 00–23
    return (val.length === 1 && num >= 3) || (val.length === 2 && num > 23);
  }
  if (part === 'hh') {
    // Hour: 01–12
    return (val.length === 1 && num >= 2 && num <= 9) || (val.length === 2 && num > 12);
  }

  if (part === 'mm' || part === 'ss') {
    // Minute or Second: 00–59
    return (val.length === 1 && num >= 6) || (val.length === 2 && num > 59);
  }
  if(part ==='AM'){
    return true
  }

  return false;
};

export const correctSegmentValue = (part, val, digitsOnly) => {
    let num = parseInt(val, 10);
    
    if (isNaN(num)) return val;

    if (part === 'YYYY') {
      const minYear = 1900;
      const maxYear = 2099;
      // Clamp to min/max instead of returning current year
      if (num < minYear) return minYear.toString().padStart(4, '0');
      if (num > maxYear) return maxYear.toString().padStart(4, '0');
      return num.toString().padStart(4, '0');
    }
  
  
    if (part === 'MM') {
      if (num < 1) return '01';
      if (num > 12) return '12';
      return num.toString().padStart(2, '0');
    }
  
    if (part === 'DD') {
      const monthStr = digitsOnly.slice(0, 2);
      const yearStr = digitsOnly.slice(4, 8);
      const maxDays = getMaxDaysInMonth(monthStr, yearStr);
      if (num < 1) return '01';
      if (num > maxDays) return maxDays.toString().padStart(2, '0');
      return num.toString().padStart(2, '0');
    }
  
    if (part === 'HH') {
      if (num < 0) return '00';
      if (num > 23) return '23';
      return num.toString().padStart(2, '0');
    }
    if (part === 'hh') {
      if (num < 1) return '01';
      if (num > 12) return '12';
      return num.toString().padStart(2, '0');
    }
  
    if (part === 'mm' || part === 'ss') {
      if (num < 0) return '00';
      if (num > 59) return '59';
      return num.toString().padStart(2, '0');
    }
    return val;
  };

/** Full English month names — longest is September (9). */
export const FULL_MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const SHORT_MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export const DATE_FORMAT_TOKEN_REGEX = /(YYYY|YY|MMMM|MMM|MM|M|DD|D|HH|mm|ss|S{1,3}|[^A-Za-z]+)/g;

export const isTextDateSegment = (part) => part === 'MMM' || part === 'MMMM';

/** Display/edit width for a format token (MMMM needs 9 for full month names). */
export const getDateSegmentDisplayLength = (part) => {
  if (part === 'MMMM') return 9;
  return part.length;
};

export const getMonthNamesForPart = (part) => {
  if (part === 'MMMM') return FULL_MONTH_NAMES;
  if (part === 'MMM') return SHORT_MONTH_NAMES;
  return [];
};

export const getTypedTextFromSegment = (segVal) => (segVal || '').replace(/_/g, '');

export const isCompleteMonthSegment = (segVal, part) => {
  const typed = getTypedTextFromSegment(segVal);
  if (!typed) return false;
  return getMonthNamesForPart(part).some(
    (name) => name.toLowerCase() === typed.toLowerCase()
  );
};

export const padTextDateSegment = (text, part) => {
  const width = getDateSegmentDisplayLength(part);
  const cleaned = getTypedTextFromSegment(text);
  const canonical =
    getMonthNamesForPart(part).find(
      (name) => name.toLowerCase() === cleaned.toLowerCase()
    ) || cleaned;
  return canonical.padEnd(width, '_').slice(0, width);
};

/**
 * Apply one letter into a MMM/MMMM segment.
 * - MMMM is always 9 chars wide; MMM is 3
 * - Uses overwrite (not splice) for stable month editing
 * - Clears on full selection / completed month retype
 * - Auto-advances only when the slot is fully filled:
 *   MMM → 3 letters, MMMM → 9 letters (e.g. September)
 * - Shorter complete months (e.g. June=4, February=8) stay in-slot;
 *   user moves next with ArrowRight / >
 */
export const applyTextDateSegmentLetter = ({
  key,
  cursor,
  selectionEnd = cursor,
  value,
  segments,
  currentSegment,
  segIndex,
}) => {
  const { start: segStart, end: segEnd, part } = currentSegment;
  const segLength = segEnd - segStart;
  const segVal = value.slice(segStart, segEnd);
  let positionInSegment = cursor - segStart;
  let segChars = segVal.split('');
  const isSegmentFullyFilled = !segChars.includes('_');
  const hasTypedText = getTypedTextFromSegment(segVal).length > 0;
  const selectionStart = Math.min(cursor, selectionEnd);
  const selectionStop = Math.max(cursor, selectionEnd);
  const wholeSegmentSelected =
    selectionStart <= segStart && selectionStop >= segEnd;

  // Start fresh when retyping a selected, completed, or full month segment
  if (
    positionInSegment === 0 &&
    hasTypedText &&
    (wholeSegmentSelected || isCompleteMonthSegment(segVal, part) || isSegmentFullyFilled)
  ) {
    segChars = Array(segLength).fill('_');
    positionInSegment = 0;
  }

  // Keep typing left-aligned in the padded month slot
  const typedLen = getTypedTextFromSegment(segChars.join('')).length;
  if (positionInSegment > typedLen) {
    positionInSegment = typedLen;
  }
  positionInSegment = Math.min(Math.max(positionInSegment, 0), segLength - 1);

  const typedChar =
    positionInSegment === 0 ? key.toUpperCase() : key.toLowerCase();

  // Deterministic overwrite (avoids splice/shift corruption while editing months)
  segChars[positionInSegment] = typedChar;
  let updatedSegment = segChars.join('').padEnd(segLength, '_');

  // Canonicalize casing when complete, but keep the full padded slot open
  if (isCompleteMonthSegment(updatedSegment, part)) {
    updatedSegment = padTextDateSegment(updatedSegment, part);
  }

  const nextValue =
    value.slice(0, segStart) + updatedSegment + value.slice(segEnd);

  const typedText = getTypedTextFromSegment(updatedSegment);
  const slotWidth = getDateSegmentDisplayLength(part);
  // Auto-next only when every letter slot is filled (MMM=3, MMMM=9)
  const shouldAdvance =
    typedText.length === slotWidth && segIndex < segments.length - 1;

  let nextSelectionStart;
  let nextSelectionEnd;
  if (shouldAdvance) {
    const next = segments[segIndex + 1];
    nextSelectionStart = next.start;
    nextSelectionEnd = next.end;
  } else {
    // Stay in month slot (e.g. June=4 / February=8) until ArrowRight / >
    nextSelectionStart = segStart + positionInSegment + 1;
    if (nextSelectionStart >= segEnd) {
      nextSelectionStart = segEnd - 1;
    }
    nextSelectionEnd = nextSelectionStart;
  }

  return {
    value: nextValue,
    selectionStart: nextSelectionStart,
    selectionEnd: nextSelectionEnd,
    shouldAdvance,
    isComplete: isCompleteMonthSegment(updatedSegment, part),
  };
};

/** Finalize text-month padding on blur and return parseable moment (if valid). */
export const finalizeMaskedDateOnBlur = (inputValue, format) => {
  const displayValue = toMaskedDateInputValue(inputValue, format);
  const momentDate = parseMaskedDateValue(displayValue, format);
  return {
    displayValue,
    momentDate,
    isValid: momentDate.isValid(),
  };
};

/** Strip underscore padding from text-month segments so moment can parse. */
export const stripTextDatePaddingForParse = (value, format) => {
  if (!value || !format) return value;
  const segments = getSegmentPositionsDateTime(format);
  let result = value;
  for (let i = segments.length - 1; i >= 0; i--) {
    const seg = segments[i];
    if (!isTextDateSegment(seg.part)) continue;
    if (seg.end > result.length) continue;
    const raw = result.slice(seg.start, seg.end);
    const cleaned = getTypedTextFromSegment(raw);
    result = result.slice(0, seg.start) + cleaned + result.slice(seg.end);
  }
  return result;
};

export const parseMaskedDateValue = (value, format, strict = true) => {
  if (!value) return moment.invalid();

  // Prefer mask-aware parse (padded MMMM/MMM slots)
  const cleaned = stripTextDatePaddingForParse(value, format);
  let parsed = moment(cleaned, format, strict);
  if (parsed.isValid()) return parsed;

  // Editing often shows Moment's natural string (no underscore padding)
  parsed = moment(String(value).replace(/_/g, ''), format, strict);
  if (parsed.isValid()) return parsed;

  parsed = moment(value, format, strict);
  if (parsed.isValid()) return parsed;

  return moment(value);
};

/**
 * Force the fixed-width edit mask (keeps MMMM 9-char slot open).
 * Safe to call on focus / before keydown while editing an existing value.
 */
export const ensureMaskedInputValue = (inputValue, format) => {
  if (!inputValue) return getDefaultValueDateTime(format);
  return toMaskedDateInputValue(inputValue, format);
};

/**
 * Remask a natural Moment display string (edit mode) and map the cursor
 * to the matching masked segment so the month slot stays open.
 */
export const remaskInputPreservingSegment = (rawValue, cursor = 0, format) => {
  const masked = ensureMaskedInputValue(rawValue, format);
  const segments = getSegmentPositionsDateTime(format);
  if (!segments.length) {
    return { masked, segment: null };
  }

  // Already masked — use cursor against masked coordinates
  if (rawValue && rawValue.length === masked.length) {
    const segment =
      segments.find((s) => cursor >= s.start && cursor <= s.end) || segments[0];
    return { masked, segment };
  }

  const parsed = parseMaskedDateValue(rawValue || '', format);
  if (!parsed.isValid()) {
    return { masked, segment: segments[0] };
  }

  // Map cursor from natural-length render onto the token, then to masked segment
  const regex = new RegExp(DATE_FORMAT_TOKEN_REGEX.source, 'g');
  let natPos = 0;
  let hitPart = null;
  let match;
  while ((match = regex.exec(format))) {
    const [part] = match;
    if (/^[A-Za-z]+$/.test(part)) {
      const rendered = String(parsed.format(part) || '');
      const start = natPos;
      const end = natPos + rendered.length;
      if (cursor >= start && cursor <= end) {
        hitPart = part;
        break;
      }
      natPos = end;
    } else {
      const start = natPos;
      const end = natPos + part.length;
      if (cursor >= start && cursor < end) {
        // Clicked a separator — prefer the following token
        hitPart = null;
      }
      natPos = end;
    }
  }

  // If separator click, pick the next token after cursor in natural string
  if (!hitPart) {
    const regex2 = new RegExp(DATE_FORMAT_TOKEN_REGEX.source, 'g');
    natPos = 0;
    let match2;
    while ((match2 = regex2.exec(format))) {
      const [part] = match2;
      if (/^[A-Za-z]+$/.test(part)) {
        const rendered = String(parsed.format(part) || '');
        const end = natPos + rendered.length;
        if (end > cursor) {
          hitPart = part;
          break;
        }
        natPos = end;
      } else {
        natPos += part.length;
      }
    }
  }

  const segment =
    (hitPart && segments.find((s) => s.part === hitPart)) || segments[0];
  return { masked, segment };
};

/** Build a fixed-width mask string from a moment date (MMMM padded to 9). */
export const formatMomentAsDateMask = (momentDate, format) => {
  if (!momentDate || typeof momentDate.format !== 'function' || !momentDate.isValid()) {
    return getDefaultValueDateTime(format);
  }
  let out = '';
  const regex = new RegExp(DATE_FORMAT_TOKEN_REGEX.source, 'g');
  let match;
  while ((match = regex.exec(format))) {
    const [part] = match;
    if (/^[A-Za-z]+$/.test(part)) {
      let formatted = momentDate.format(part);
      const width = getDateSegmentDisplayLength(part);
      if (isTextDateSegment(part)) {
        out += padTextDateSegment(formatted, part);
      } else {
        if (formatted.length < width) {
          formatted = formatted.padStart(width, '0');
        }
        out += formatted.slice(0, width);
      }
    } else {
      out += part;
    }
  }
  return out;
};

/** Normalize whatever is in the input into the editable fixed-width mask. */
export const toMaskedDateInputValue = (inputValue, format) => {
  if (!inputValue) return getDefaultValueDateTime(format);

  const expectedLen = getDefaultValueDateTime(format).length;

  // Already masked length — still re-pad text months so the slot cannot collapse
  if (inputValue.length === expectedLen) {
    const segments = getSegmentPositionsDateTime(format);
    let result = inputValue;
    segments.forEach((seg) => {
      if (!isTextDateSegment(seg.part)) return;
      const raw = result.slice(seg.start, seg.end);
      const padded = padTextDateSegment(raw, seg.part);
      result = result.slice(0, seg.start) + padded + result.slice(seg.end);
    });
    return result;
  }

  // Natural Moment display while editing (e.g. "July 9, 2026 22:22:22")
  let parsed = moment(inputValue, format, true);
  if (!parsed.isValid()) {
    parsed = moment(String(inputValue).replace(/_/g, ''), format, true);
  }
  if (!parsed.isValid()) {
    parsed = moment(inputValue);
  }
  if (parsed.isValid()) {
    return formatMomentAsDateMask(parsed, format);
  }
  return getDefaultValueDateTime(format);
};

export const getSegmentPositions = (format , type=null) => {
 if (type === 'time') {
    // Assuming format = 'HH:mm A' or 'hh:mm A'
    const parts = [
      { part: 'hh', type: 'hour' },
      { part: 'mm', type: 'minute' },
      { part: 'AM', type: 'ampm' }, // Treat as 2 characters!
    ];
    const separators = [':', ' ']; // Separators between segments
    let pos = 0;
    return parts.map((segment, index) => {
      const start = pos;
      const end = start + segment.part.length;
      pos = end + (separators[index] ? separators[index].length : 0);
      return { part: segment.part, start, end, type: segment.type };
    });
  }
  const separatorMatch = format.match(/[^A-Za-z0-9]/);
  const separator = separatorMatch ? separatorMatch[0] : '/';
  const parts = format.split(separator);
  let pos = 0;

  return parts.map(part => {
    const len = getDateSegmentDisplayLength(part);
    const start = pos;
    const end = start + len;
    pos = end + separator.length;
    return { part, start, end };
  });
};

export const getSegmentPositionsDateTime = (format) => {
  const segments = [];
  let pos = 0;

  // Match known format tokens or separators
  const regex = new RegExp(DATE_FORMAT_TOKEN_REGEX.source, 'g');

  let match;

  while ((match = regex.exec(format))) {
    const [part] = match;
    const isToken = /^[A-Za-z]+$/.test(part);
    const len = isToken ? getDateSegmentDisplayLength(part) : part.length;
    const start = pos;
    const end = start + len;

    if (isToken) {
      segments.push({ part, start, end });
    }

    pos = end;
  }

  return segments;
};

export const getDefaultValue = (format, type=null) => {
  if (type === 'time') {
    // For format like "HH:mm A" → default: "__:__ __"
    return '__:__ __';
  }
  const separator = format.match(/[^A-Za-z0-9]/)?.[0] || '/';
  const parts = format.split(separator);
  // console.log('parts',parts);
  
  return parts.map(p => '_'.repeat(getDateSegmentDisplayLength(p))).join(separator);
};

export const getDefaultValueDateTime = (format) => {
  let out = '';
  const regex = new RegExp(DATE_FORMAT_TOKEN_REGEX.source, 'g');
  let match;
  while ((match = regex.exec(format))) {
    const [part] = match;
    if (/^[A-Za-z]+$/.test(part)) {
      out += '_'.repeat(getDateSegmentDisplayLength(part));
    } else {
      out += part;
    }
  }
  return out;
};
